import React from "react";
import { Container, Box, Typography, Tabs, Tab } from "@mui/material";
import Footer from "../components/Footer";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scanner-legal-tabpanel-${index}`}
      aria-labelledby={`scanner-legal-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const ScannerLegal: React.FC = () => {
  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Container maxWidth="md" sx={{ py: 4, flex: 1 }}>
        <Typography
          variant="h3"
          sx={{ fontWeight: 700, mb: 3, textAlign: "center" }}
        >
          I am Scanner Legal
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs value={value} onChange={handleChange} centered>
            <Tab label="Terms & Conditions" />
            <Tab label="Privacy Policy" />
          </Tabs>
        </Box>

        <TabPanel value={value} index={0}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Terms and Conditions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Last updated: October 3, 2026
          </Typography>

          <Box
            sx={{
              "& h5": { fontSize: "1.25rem", fontWeight: 600, mt: 3, mb: 1.5 },
              "& p, & ul": { lineHeight: 1.8, mb: 2 },
            }}
          >
            <Typography variant="h5">1. Acceptance of Terms</Typography>
            <Typography variant="body1">
              By downloading, installing, or using I am Scanner ("the App"),
              you agree to be bound by these Terms and Conditions. If you do not
              agree to these terms, please do not use the App.
            </Typography>

            <Typography variant="h5">2. Description of Service</Typography>
            <Typography variant="body1">
              I am Scanner is a document scanning application that allows users
              to scan, crop, edit, annotate, OCR, translate, and export
              documents. The App includes features such as:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Document scanning using device camera</li>
              <li>Image editing and filtering</li>
              <li>PDF generation and export</li>
              <li>OCR (Optical Character Recognition)</li>
              <li>Translation</li>
              <li>Encrypted vault for sensitive documents</li>
              <li>Biometric app lock</li>
              <li>Signature pad</li>
              <li>Ad-supported features</li>
            </Box>

            <Typography variant="h5">3. User Responsibilities</Typography>
            <Typography variant="body1">You agree to:</Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Use the App only for lawful purposes</li>
              <li>
                Not use the App to scan or store illegal, harmful, or offensive
                content
              </li>
              <li>
                Not attempt to reverse engineer, decompile, or disassemble the
                App
              </li>
              <li>
                Not use the App in any way that could damage, disable, or impair
                the App
              </li>
              <li>Comply with all applicable laws and regulations</li>
              <li>Respect the rights of others</li>
            </Box>

            <Typography variant="h5">4. Data Storage and Ownership</Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>
                All scanned documents and data are stored locally on your device
              </li>
              <li>
                The App works 100% offline and does not automatically upload
                your documents to any cloud servers
              </li>
              <li>
                You retain full ownership of all documents you create or scan
                using the App
              </li>
              <li>
                Encrypted documents in the Vault remain encrypted and are only
                accessible through authentication on your device
              </li>
            </Box>

            <Typography variant="h5">5. Permissions</Typography>
            <Typography variant="body1">
              The App requires certain permissions to function properly:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>
                Camera permission to scan documents
              </li>
              <li>
                Storage permissions to save and access documents
              </li>
              <li>
                Biometric permission for app lock and vault authentication
              </li>
            </Box>

            <Typography variant="h5">6. Advertisements</Typography>
            <Typography variant="body1">
              I am Scanner is ad-supported to provide the app for free. The App
              uses Google Mobile Ads. By using the App, you agree to the
              displaying of advertisements. Some features may be unlocked by
              watching rewarded ads.
            </Typography>

            <Typography variant="h5">7. Third-Party Services</Typography>
            <Typography variant="body1">
              The App may use third-party services including:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>
                Google Mobile Ads - for advertisements
              </li>
              <li>
                Google ML Kit - for OCR and translation (processed on-device
                where possible)
              </li>
            </Box>
            <Typography variant="body1">
              Use of these services is subject to their respective terms and
              privacy policies.
            </Typography>

            <Typography variant="h5">8. Disclaimers</Typography>
            <Typography variant="body1">
              The App is provided "as is" without any warranties, express or
              implied. We do not guarantee the accuracy, completeness, or
              reliability of OCR results, translations, or any other features.
              The App is not intended to be used for legal documents requiring
              certified scanning - use at your own discretion.
            </Typography>

            <Typography variant="h5">9. Limitation of Liability</Typography>
            <Typography variant="body1">
              In no event shall the developers of I am Scanner be liable for any
              direct, indirect, incidental, special, consequential, or punitive
              damages arising from the use of or inability to use the App,
              including but not limited to loss of documents, data, or profits.
            </Typography>

            <Typography variant="h5">10. Modifications</Typography>
            <Typography variant="body1">
              We reserve the right to modify or discontinue the App at any time
              without notice. We may also update these Terms and Conditions from
              time to time. Continued use of the App after changes constitutes
              acceptance of the modified terms.
            </Typography>

            <Typography variant="h5">11. Governing Law</Typography>
            <Typography variant="body1">
              These terms and conditions are governed by and construed in
              accordance with applicable laws.
            </Typography>

            <Typography variant="h5">12. Contact Us</Typography>
            <Typography variant="body1">
              If you have any questions about these Terms and Conditions, please
              contact us at sharmashashi940@gmail.com
            </Typography>
          </Box>
        </TabPanel>

        <TabPanel value={value} index={1}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Privacy Policy
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Last updated: October 3, 2026
          </Typography>

          <Box
            sx={{
              "& h5": { fontSize: "1.25rem", fontWeight: 600, mt: 3, mb: 1.5 },
              "& p, & ul": { lineHeight: 1.8, mb: 2 },
            }}
          >
            <Typography variant="h5">1. Introduction</Typography>
            <Typography variant="body1">
              This Privacy Policy describes how I am Scanner ("we", "us", or
              "our") collects, uses, and protects your information when you use
              the App. We are committed to protecting your privacy and handling
              your data responsibly.
            </Typography>

            <Typography variant="h5">2. Information We Collect</Typography>
            <Typography variant="body1">
              I am Scanner is designed to be fully offline and privacy-focused.
              We do not collect, transmit, or store your documents on our
              servers.
            </Typography>

            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              Local Data Only
            </Typography>
            <Typography variant="body1">
              All data generated or captured by the App remains on your device:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Scanned images and documents</li>
              <li>PDF files you create</li>
              <li>App settings and preferences</li>
              <li>Encrypted vault contents</li>
            </Box>

            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              Automatically Collected Information
            </Typography>
            <Typography variant="body1">
              The App itself does not collect personal identifiers.
              However, third-party services integrated into the App may collect
              certain information:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>
                Google Mobile Ads may collect device identifiers, IP addresses,
                and usage data for ad personalization (subject to Google's
                Privacy Policy)
              </li>
              <li>
                Crash reports may be collected by your device's app store or
                system if enabled
              </li>
            </Box>

            <Typography variant="h5">3. How We Use Information</Typography>
            <Typography variant="body1">
              Since we don't collect your personal data, we don't use it for
              marketing, analytics, or profiling. Local data is used solely to
              provide the functionality of the App on your device.
            </Typography>

            <Typography variant="h5">4. Data Security</Typography>
            <Typography variant="body1">
              We take data security seriously:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>
                Documents in the Vault are encrypted using industry-standard
                encryption
              </li>
              <li>
                App Lock uses your device's biometric authentication (fingerprint,
                face ID, or screen lock)
              </li>
              <li>
                All data stays on your device - we have no access to your
                documents
              </li>
              <li>
                We recommend keeping your device secure with a strong passcode
              </li>
            </Box>

            <Typography variant="h5">5. Third-Party Services</Typography>
            <Typography variant="body1">
              Third-party services have their own privacy policies:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>
                Google Mobile Ads:{" "}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://policies.google.com/privacy
                </a>
              </li>
              <li>
                Google ML Kit:{" "}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://policies.google.com/privacy
                </a>
              </li>
            </Box>

            <Typography variant="h5">6. Permissions and Device Features</Typography>
            <Typography variant="body1">
              Camera, storage, and biometric data are used only for the
              functionality you request. We never access these features without
              your explicit permission.
            </Typography>

            <Typography variant="h5">7. Children's Privacy</Typography>
            <Typography variant="body1">
              The App is not intended for children under 13. We do not knowingly
              collect personal information from children under 13. If you are a
              parent or guardian and believe your child has provided us with
              personal information, please contact us so we can take
              appropriate action.
            </Typography>

            <Typography variant="h5">8. Your Rights</Typography>
            <Typography variant="body1">
              Since all data is stored locally on your device, you have complete
              control:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>You can delete any document at any time</li>
              <li>You can clear all app data through your device settings</li>
              <li>You can uninstall the App to remove all associated data</li>
              <li>
                You can revoke permissions at any time through device settings
              </li>
            </Box>

            <Typography variant="h5">9. Changes to This Privacy Policy</Typography>
            <Typography variant="body1">
              We may update this Privacy Policy from time to time. We will
              notify you of any changes by posting the new Privacy Policy in
              this location and updating the "Last updated" date. Continued use
              of the App after changes constitutes acceptance of the updated
              policy.
            </Typography>

            <Typography variant="h5">10. Contact Us</Typography>
            <Typography variant="body1">
              If you have any questions about this Privacy Policy, please
              contact us at sharmashashi940@gmail.com
            </Typography>
          </Box>
        </TabPanel>
      </Container>
      <Footer />
    </Box>
  );
};

export default ScannerLegal;
